import React, { useState } from "react";
import { Save } from "lucide-react";
import MultiSelect from "../../components/UI/MultiSelect";

const PolicyForm = ({ closeModal, onSubmit, initialValues = {} }) => {
  const [activeTab, setActiveTab] = useState(1);
  const [formData, setFormData] = useState(initialValues);

  const tabs = [
    { id: 1, name: "Basic Information" },
    { id: 2, name: "Entitlement" },
    { id: 3, name: "Balance Rules" },
    { id: 4, name: "Application" },
    { id: 5, name: "Restrictions" },
    { id: 6, name: "Exit Rules" },
    { id: 7, name: "Documentation" },
    { id: 8, name: "System Setup" },
  ];

  const handleMultiSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(formData);
  };

  const nextTab = () => {
    if (activeTab < tabs.length) setActiveTab(activeTab + 1);
  };

  const prevTab = () => {
    if (activeTab > 1) setActiveTab(activeTab - 1);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col flex-1 min-h-0 bg-white dark:bg-gray-900 font-poppins"
    >
      <div className="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-800 overflow-x-auto custom-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
              activeTab === tab.id
                ? "border-blue-600 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:border-gray-300"
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        {activeTab === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leave Code
              </label>
              <input
                type="text"
                name="leave_code"
                maxLength={4}
                value={formData.leave_code || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Ex. SICK"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leave Name
              </label>
              <input
                type="text"
                name="leave_name"
                maxLength={50}
                value={formData.leave_name || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Ex. Sick Leave"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Effective Date
              </label>
              <input
                type="date"
                name="effective_date"
                value={formData.effective_date || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Applies To
              </label>
              <select
                name="applies_to"
                value={formData.applies_to || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="All">All Employees</option>
                <option value="Specific">Specific Department</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Employee Type
              </label>
              <MultiSelect
                options={["Full-Time", "Part-Time", "Contract", "Intern"]}
                value={formData.employee_type || []}
                onChange={(val) =>
                  handleMultiSelectChange("employee_type", val)
                }
                placeholder="Select employee types..."
              />
            </div>
          </div>
        )}

        {activeTab === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Days Per Year
              </label>
              <input
                type="number"
                name="days_per_year"
                value={formData.days_per_year || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Accrual Frequency
              </label>
              <select
                name="accrual_frequency"
                value={formData.accrual_frequency || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Accrual Rate
              </label>
              <input
                type="number"
                step="0.01"
                name="accrual_rate"
                value={formData.accrual_rate || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="pro_rata_new_joiners"
                  checked={formData.pro_rata_new_joiners || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Pro-rata for New Joiners
              </label>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Credited On
              </label>
              <select
                name="credited_on"
                value={formData.credited_on || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Start of Month">Start of Month</option>
                <option value="End of Month">End of Month</option>
                <option value="Start of Year">Start of Year</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 3 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Carry Forward
              </label>
              <input
                type="number"
                name="max_carry_forward"
                value={formData.max_carry_forward || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Absolute Cap
              </label>
              <input
                type="number"
                name="absolute_cap"
                value={formData.absolute_cap || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Excess Beyond Cap
              </label>
              <select
                name="excess_beyond_cap"
                value={formData.excess_beyond_cap || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Lapse">Lapse</option>
                <option value="Encash">Encash</option>
              </select>
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="encashment_allowed"
                  checked={formData.encashment_allowed || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Encashment Allowed
              </label>
            </div>
            {formData.encashment_allowed && (
              <>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    When Encashable
                  </label>
                  <select
                    name="when_encashable"
                    value={formData.when_encashable || ""}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  >
                    <option value="">Select option...</option>
                    <option value="Year End">Year End</option>
                    <option value="Resignation">Resignation</option>
                    <option value="Anytime">Anytime</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Encashment Limit
                  </label>
                  <input
                    type="number"
                    name="encashment_limit"
                    value={formData.encashment_limit || ""}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Encashment Formula
                  </label>
                  <select
                    name="encashment_formula"
                    value={formData.encashment_formula || ""}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  >
                    <option value="">Select option...</option>
                    <option value="Basic / 30">Basic / 30</option>
                    <option value="Gross / 30">Gross / 30</option>
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Apply Via
              </label>
              <select
                name="apply_via"
                value={formData.apply_via || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Self Service">Self Service (ESS)</option>
                <option value="Manager">Manager</option>
                <option value="HR">HR Only</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Advance Notice Required
              </label>
              <select
                name="advance_notice_required"
                value={formData.advance_notice_required || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notice: 1 Day Leave
              </label>
              <input
                type="number"
                name="notice_1_day"
                value={formData.notice_1_day || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Days"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notice: 3 Days Leave
              </label>
              <input
                type="number"
                name="notice_3_days"
                value={formData.notice_3_days || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Days"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notice: 5 Days Leave
              </label>
              <input
                type="number"
                name="notice_5_days"
                value={formData.notice_5_days || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Days"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Approval Flow
              </label>
              <input
                type="text"
                name="approval_flow"
                value={formData.approval_flow || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Ex. Manager -> HR"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Allowed in Notice Period
              </label>
              <select
                name="allowed_notice_period"
                value={formData.allowed_notice_period || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Subject to Approval">Subject to Approval</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 5 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Can Combine With
              </label>
              <MultiSelect
                options={[
                  "Casual Leave",
                  "Sick Leave",
                  "Earned Leave",
                  "Comp Off",
                  "None",
                ]}
                value={formData.can_combine_with || []}
                onChange={(val) =>
                  handleMultiSelectChange("can_combine_with", val)
                }
                placeholder="Select combinable leaves..."
              />
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="sandwich_rule_applies"
                  checked={formData.sandwich_rule_applies || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Sandwich Rule Applies
              </label>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sandwich Deduction Priority
              </label>
              <select
                name="sandwich_deduction_priority"
                value={formData.sandwich_deduction_priority || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Current Leave">Current Leave</option>
                <option value="Loss of Pay">Loss of Pay</option>
                <option value="Other Leaves">Other Leaves</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Blackout Periods
              </label>
              <input
                type="text"
                name="blackout_periods"
                value={formData.blackout_periods || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Ex. 25 Dec to 31 Dec"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Consecutive Days
              </label>
              <input
                type="number"
                name="max_consecutive_days"
                value={formData.max_consecutive_days || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
          </div>
        )}

        {activeTab === 6 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Eligible on Notice Period
              </label>
              <select
                name="exit_eligible_notice_period"
                value={formData.exit_eligible_notice_period || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Pro-rata basis">Pro-rata basis</option>
              </select>
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="balance_settled_fnf"
                  checked={formData.balance_settled_fnf || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Balance Settled in F&F
              </label>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Calculated Till
              </label>
              <select
                name="calculated_till"
                value={formData.calculated_till || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Last Working Day">Last Working Day</option>
                <option value="Resignation Date">Resignation Date</option>
              </select>
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="forfeited_terminated"
                  checked={formData.forfeited_terminated || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Forfeited if Terminated
              </label>
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="negative_balance_deduction"
                  checked={formData.negative_balance_deduction || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Negative Balance Deduction
              </label>
            </div>
          </div>
        )}

        {activeTab === 7 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Medical Certificate
              </label>
              <select
                name="medical_certificate"
                value={formData.medical_certificate || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Not Required">Not Required</option>
                <option value="Required after 2 days">
                  Required after 2 days
                </option>
                <option value="Always Required">Always Required</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Special Approvals Needed
              </label>
              <MultiSelect
                options={[
                  "HOD Approval",
                  "HR Head Approval",
                  "Management Approval",
                ]}
                value={formData.special_approvals_needed || []}
                onChange={(val) =>
                  handleMultiSelectChange("special_approvals_needed", val)
                }
                placeholder="Select special approvals..."
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prior Approval for Holiday Work
              </label>
              <select
                name="prior_approval_holiday_work"
                value={formData.prior_approval_holiday_work || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="">Select option...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Manager Discretion">Manager Discretion</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 8 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                HRMS Module Code
              </label>
              <input
                type="text"
                name="hrms_module_code"
                value={formData.hrms_module_code || ""}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="Ex. LEAVE001"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Color Code
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  name="color_code"
                  value={formData.color_code || "#3b82f6"}
                  onChange={handleChange}
                  className="h-10 w-10 border-0 p-0"
                />
                <input
                  type="text"
                  value={formData.color_code || "#3b82f6"}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex-1"
                />
              </div>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Alerts
              </label>
              <MultiSelect
                options={["On Apply", "On Approve", "On Reject", "To HR"]}
                value={formData.email_alerts || []}
                onChange={(val) => handleMultiSelectChange("email_alerts", val)}
                placeholder="Select email alerts..."
              />
            </div>
            <div className="col-span-1 flex flex-col justify-center mt-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  name="payroll_integration"
                  checked={formData.payroll_integration || false}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                />
                Payroll Integration
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50 mt-auto">
        <button
          type="button"
          onClick={closeModal}
          className="px-6 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          Close
        </button>

        <div className="flex space-x-3">
          {activeTab > 1 && (
            <button
              type="button"
              onClick={prevTab}
              className="px-6 py-2.5 rounded-lg border border-blue-600 text-blue-600 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Previous
            </button>
          )}

          {activeTab < tabs.length ? (
            <button
              type="button"
              onClick={nextTab}
              className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all shadow-lg shadow-blue-500/20"
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all shadow-lg shadow-blue-500/20 flex items-center"
            >
              <Save size={18} className="mr-2" />
              Save Policy
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default PolicyForm;
